import { useCallback, useEffect, useRef } from "react";
import { flushSync } from "react-dom";

const TRANSITION_NAME = "conversation-name";
const LIST_SELECTOR = "[data-conversation-list]";

function conversationName(roomId: string): HTMLElement | null {
  return (
    [...document.querySelectorAll<HTMLElement>("[data-conversation-name]")].find(
      (element) => element.dataset.conversationName === roomId && visible(element),
    ) ?? null
  );
}

function visible(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  const list = element.closest(LIST_SELECTOR)?.getBoundingClientRect();
  return (
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.top < window.innerHeight &&
    rect.bottom > (list?.top ?? 0) &&
    rect.top < (list?.bottom ?? window.innerHeight)
  );
}

export function useConversationTransition() {
  const generationRef = useRef(0);
  const originRef = useRef<{ roomId: string; scrollTop: number } | null>(null);
  const transitionRef = useRef<ViewTransition | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      generationRef.current += 1;
      transitionRef.current?.skipTransition();
      cleanupRef.current?.();
    },
    [],
  );

  const run = useCallback((roomId: string, update: () => void, restore?: () => void) => {
    const generation = ++generationRef.current;
    transitionRef.current?.skipTransition();
    cleanupRef.current?.();
    const source = conversationName(roomId);
    let updated = false;
    const updateView = () => {
      updated = true;
      if (source) source.style.viewTransitionName = "";
      flushSync(update);
      restore?.();
    };
    if (
      !source ||
      !visible(source) ||
      typeof document.startViewTransition !== "function" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      updateView();
      return;
    }

    source.style.viewTransitionName = TRANSITION_NAME;
    document.documentElement.dataset.conversationTransition = "active";
    let target: HTMLElement | null = null;
    const cleanup = () => {
      source.style.viewTransitionName = "";
      if (target) target.style.viewTransitionName = "";
      delete document.documentElement.dataset.conversationTransition;
      cleanupRef.current = null;
      transitionRef.current = null;
    };
    cleanupRef.current = cleanup;
    let transition: ViewTransition;
    try {
      transition = document.startViewTransition(() => {
        if (generationRef.current !== generation) return;
        updateView();
        target = conversationName(roomId);
        const level = target?.closest<HTMLElement>(".chat-mobile-level");
        if (level) level.dataset.conversationMotion = "true";
        if (target && visible(target)) target.style.viewTransitionName = TRANSITION_NAME;
      });
    } catch {
      cleanup();
      if (!updated) updateView();
      return;
    }
    transitionRef.current = transition;
    const finish = () => {
      // An interrupted transition must not clean up its replacement.
      if (transitionRef.current === transition) cleanup();
    };
    void transition.finished.then(finish, finish);
  }, []);

  const open = useCallback(
    (roomId: string, update: () => void) => {
      const list = conversationName(roomId)?.closest<HTMLElement>(LIST_SELECTOR);
      originRef.current = list ? { roomId, scrollTop: list.scrollTop } : null;
      run(roomId, update);
    },
    [run],
  );

  const close = useCallback(
    (roomId: string, update: () => void) => {
      const origin = originRef.current;
      originRef.current = null;
      if (origin?.roomId !== roomId) {
        flushSync(update);
        return;
      }
      run(roomId, update, () => {
        const list = document.querySelector<HTMLElement>(LIST_SELECTOR);
        if (list) list.scrollTop = origin.scrollTop;
        conversationName(roomId)?.closest<HTMLElement>("button")?.focus({ preventScroll: true });
      });
    },
    [run],
  );

  return { open, close };
}
